/**
 * 下载工具函数
 * 支持单个或多个 tab 的 Excel 下载，多个 tab 自动分为多个 sheet
 * 格式处理：直接使用 format 作为 Excel 格式字符串
 */
import {
  exportExcel,
  formatExeclCommon as formatExcelCommon,
  sanitizeExcelSheetName,
} from '@/pages/dashboard/utils/utils';
import { ArtColumn } from 'ali-react-table';
import moment from 'moment';
import type {
  IColumnFormatConfig,
  ITabDataConfig,
  IDownloadConfig,
} from './_type';
import {
  EXCEL_DEFAULT_COL_WIDTH,
  EXCEL_DEFAULT_FONT_SIZE,
  EXCEL_DEFAULT_FONT_NAME_WINDOWS,
  EXCEL_DEFAULT_FONT_NAME_MAC,
  EXCEL_HEADER_BG_COLOR,
  EXCEL_HEADER_TEXT_COLOR,
} from './_constant';
import { getName } from '@home/utils/common';

// 重新导出类型，保持向后兼容
export type {
  IColumnFormatConfig,
  ITabDataConfig,
  IDownloadConfig,
} from './_type';

/**
 * 判断是否是日期格式字符串
 */
const isDateFormatString = (format: string): boolean => /yyyy|mm|dd|hh|ss/i.test(format);

/**
 * 将 Excel 日期格式字符串转换为 moment 格式字符串
 * Excel: yyyy-mm-dd -> moment: YYYY-MM-DD
 */
const convertExcelDateFormatToMoment = (excelFormat: string): string =>
  excelFormat
    .replace(/yyyy/gi, 'YYYY')
    .replace(/mm/g, 'MM')
    .replace(/dd/gi, 'DD')
    .replace(/hh/gi, 'HH')
    .replace(/ss/gi, 'SS');

/**
 * 判断是否是 Excel 格式字符串
 * Excel 格式字符串特征：
 * - 日期格式：包含 yyyy, mm, dd, hh 等
 * - 数字格式：包含 #, 0, %, , 等
 * - 文本格式：@
 */
const isExcelFormatString = (format: string): boolean => {
  // 先判断日期格式（复用 isDateFormatString）
  if (isDateFormatString(format)) {
    return true;
  }
  // Excel 数字格式：包含 #, 0, %, , 等
  if (/[#0%,]/.test(format)) {
    return true;
  }
  // Excel 文本格式
  if (format === '@') {
    return true;
  }
  return false;
};

/**
 * 判断是否是数字类型
 */
const isNumericValue = (value: any): boolean =>
  typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)) && value !== '');

/**
 * 转换为数字值
 */
const toNumericValue = (value: any): number => (typeof value === 'number' ? value : Number(value));

/**
 * 创建字符串格式对象
 */
const createStringFormat = (value: any, format = '@'): any => ({
  t: 's',
  v: String(value),
  z: format,
});

/**
 * 创建数字格式对象
 */
const createNumberFormat = (value: any, format: string): any => ({
  t: 'n',
  v: toNumericValue(value),
  z: format,
});

/**
 * 根据格式化规则或函数格式化值
 * @param value 要格式化的值
 * @param format 格式化规则（字符串）或自定义格式化函数
 * @param record 记录对象
 */
const formatValueByRule = (
  value: any,
  format?: string | ((value: any, record: any, isEn?: boolean) => any),
  record?: any,
  isEn?: boolean,
): any => {
  // 如果是函数，直接调用
  if (typeof format === 'function') {
    return format(value, record, isEn ?? false);
  }

  // 如果值无效，返回 '-'
  if (value === null || value === undefined || value === '') {
    return formatExcelCommon('-' as any, false);
  }

  // 如果没有格式化规则，使用默认格式化
  if (!format) {
    return formatExcelCommon(value as any, false);
  }

  // 判断是否是 Excel 格式字符串（直接使用，不进行映射）
  if (isExcelFormatString(format)) {
    // 日期格式
    if (isDateFormatString(format)) {
      const date = moment(value);
      if (date.isValid()) {
        return {
          t: 'd',// 日期类型
          v: date.toDate(),
          z: format,// 直接使用 format 作为 Excel 格式
        };
      }
      // 无效日期，返回字符串格式
      return createStringFormat(value);
    }

    // 数字格式：先检查 format 是否是数字格式
    const isNumberFormat = /[#0]/.test(format) && !isDateFormatString(format);
    if (isNumberFormat) {
      if (isNumericValue(value)) {
        return createNumberFormat(value, format);
      }
      // 不是数字，返回字符串格式
      return createStringFormat(value);
    }

    // 文本格式
    if (format === '@') {
      return createStringFormat(value, format);
    }

    // 其他 Excel 格式字符串（如百分比格式）
    if (isNumericValue(value)) {
      return createNumberFormat(value, format);
    }

    // 非数字值，作为字符串处理
    return createStringFormat(value, format);
  }

  // 普通字符串格式（向后兼容：使用默认格式化）
  return formatExcelCommon(value as any, false);
};

/**
 * 生成 Excel 列名（A, B, C, ..., Z, AA, AB, ...）
 */
const buildExcelColumns = (length: number): string[] => {
  const columns: string[] = [];
  for (let i = 0;i < length;i++) {
    let column = '';
    let num = i;
    while (num >= 0) {
      column = String.fromCharCode(65 + (num % 26)) + column;
      num = Math.floor(num / 26) - 1;
    }
    columns.push(column);
  }
  return columns;
};

/**
 * 从列配置中提取列宽数组（优化版）
 * @param columns 列配置数组
 * @returns 列宽数组（像素）
 */
const extractColWidths = (columns: IColumnFormatConfig[]): number[] | undefined => {
  // 先检查是否有任何列配置了宽度，避免不必要的 map 操作
  const hasAnyWidth = columns.some((column) => column.width !== undefined);
  if (!hasAnyWidth) {
    return undefined;
  }
  // 返回列宽数组，未配置的使用 undefined
  return columns.map((column) => column.width) as number[];
};

/**
 * 转换列宽数组为 Excel 格式
 */
const convertColWidthsToExcel = (colWidths: number[]): { wpx: number; }[] =>
  colWidths.map((width) => ({
    wpx: width ?? EXCEL_DEFAULT_COL_WIDTH
  }));

/**
 * 转换单个 tab 数据为 Excel 格式
 * @param tabConfig Tab 数据配置
 */
const convertTabDataToExcel = (
  tabConfig: ITabDataConfig,
  isEN?: boolean,
): any[] => {
  const { dataSource, columns, headerRows } = tabConfig;
  if (!Array.isArray(dataSource) || dataSource.length === 0) {
    return [{}];
  }
  const excelColumn = buildExcelColumns(columns.length);

  const resolveColName = (column: IColumnFormatConfig): string => {
    if (isEN && column.nameEn) return column.nameEn;
    if (!isEN && column.nameCn) return column.nameCn;
    return column.name;
  };

  // 如果有多行表头配置，使用自定义表头
  if (headerRows && headerRows.length > 0) {
    const excelData: any[] = [];

    // 添加多行表头
    headerRows.forEach((row) => {
      const headerRow: any = {};
      row.forEach((cellValue, index) => {
        headerRow[excelColumn[index]] = cellValue;
      });
      excelData.push(headerRow);
    });

    // 添加数据行
    dataSource.forEach((record) => {
      const rowData: any = {};
      columns?.forEach((column, index) => {
        const value = record?.[column?.code];
        const formattedValue = formatValueByRule(
          value,
          column?.format,
          record,
          isEN,
        );
        rowData[excelColumn[index]] = formattedValue;
      });
      excelData.push(rowData);
    });

    return excelData;
  }

  // 默认单行表头处理（保持向后兼容）
  const headerRow: any = {};
  columns?.forEach((column, index) => {
    headerRow[excelColumn[index]] = resolveColName(column);
  });

  // 构建数据行
  const excelData: any[] = [headerRow];
  dataSource.forEach((record) => {
    const rowData: any = {};
    columns?.forEach((column, index) => {
      const value = record?.[column?.code];
      const formattedValue = formatValueByRule(
        value,
        column?.format,
        record,
        isEN,
      );
      rowData[excelColumn[index]] = formattedValue;
    });
    excelData.push(rowData);
  });
  return excelData;
};

/**
 * 导出 Excel（支持单个或多个 tab，每个 tab 一个 sheet）
 */
const downloadExcelInternal = async (
  config: IDownloadConfig,
  isEN?: boolean,
): Promise<void> => {
  if (!config.tabs || config.tabs.length === 0) {
    throw new Error('tabs 配置不能为空');
  }

  // 单个 tab 时使用 exportExcel（保持向后兼容）
  if (config.tabs.length === 1) {
    const tabConfig = config.tabs[0];
    const excelData = convertTabDataToExcel(tabConfig, isEN);
    const fileName = generateFileName(config.fileName || tabConfig.tabName);
    const colWidths = extractColWidths(tabConfig.columns);
    // 转换列宽格式为 Excel 格式
    const excelColWidths = colWidths && colWidths.length > 0
      ? convertColWidthsToExcel(colWidths)
      : undefined;
    // 直接使用 tabName，exportExcel 内部会处理长度限制
    await exportExcel(
      fileName,
      excelData as never[],
      excelColWidths as never[],
      tabConfig.tabName,
      tabConfig.columns,
    );
    return;
  }

  // 多个 tab 时使用 xlsx-js-style
  // 静态导入会包含在初始 bundle 中，即使不使用也会加载，所以这里进行动态导入
  const XLSX = await import('xlsx-js-style');
  const wb = XLSX.utils.book_new();

  // 为每个 tab 创建一个 sheet
  config.tabs.forEach((tabConfig) => {
    const excelData = convertTabDataToExcel(tabConfig, isEN);

    // 设置 Excel 样式
    setExportExcelDefaultStyle(
      excelData,
      tabConfig.headerRows,
      tabConfig.columns,
    );

    // 转换为 sheet
    const ws = XLSX.utils.json_to_sheet(excelData, { header: [], skipHeader: true });

    // 设置合并单元格
    if (tabConfig.mergeCells && tabConfig.mergeCells.length > 0) {
      ws['!merges'] = tabConfig.mergeCells;
    }

    // 设置列宽
    const colWidths = extractColWidths(tabConfig.columns);
    if (colWidths && colWidths.length > 0) {
      ws['!cols'] = convertColWidthsToExcel(colWidths);
    } else {
      setExportExcelDefaultColWidths(ws, excelData);
    }

    // 添加 sheet 到 workbook
    const sheetName = limitSheetName(tabConfig.tabName);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  // 生成文件名
  const timestamp = moment().format('YYYY_MM_DD HH_mm_ss');
  const fileName = generateFileName(config.fileName);

  // 导出文件
  XLSX.writeFile(wb, `${fileName} ${timestamp}.xlsx`);
};

/**
 * 获取 Excel 字体名称（根据操作系统）
 */
const getExcelFontName = (): string =>
  typeof window !== 'undefined' && /Windows/.test(navigator.userAgent)
    ? EXCEL_DEFAULT_FONT_NAME_WINDOWS
    : EXCEL_DEFAULT_FONT_NAME_MAC;

/** 长度 + 非法字符（与 exportExcel 单 tab 路径一致） */
const limitSheetName = (name: string): string =>
  sanitizeExcelSheetName(name);

/**
 * 生成文件名（处理空格和下划线）
 */
const generateFileName = (fileName?: string, defaultName = 'download'): string =>
  (fileName || defaultName)?.replace(/\u0020/g, '_') || 'download';

/**
 * 设置 Excel 默认样式（和 utils.ts 保持一致）
 */
const setExportExcelDefaultStyle = (
  data: unknown[],
  headerRows?: unknown[][],
  columns?: IColumnFormatConfig[],
): void => {
  if (!data || data.length === 0) return;

  const cols = Object.keys(data[0] ?? {});
  const fontName = getExcelFontName();

  const defaultStyleAlign = {
    alignment: {
      vertical: 'center',
      horizontal: 'center',
    },
  };

  const wrapTextBodyAlign = {
    alignment: {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true,
    },
  };

  const defaultStyleFont = {
    font: {
      sz: EXCEL_DEFAULT_FONT_SIZE,
      name: fontName,
    },
  };

  const defaultStyleBorder = {
    border: {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' },
    },
  };

  const headDefaultStyle = {
    ...defaultStyleAlign,
    ...defaultStyleBorder,
    font: {
      sz: EXCEL_DEFAULT_FONT_SIZE,
      name: fontName,
      bold: true,
      color: { rgb: EXCEL_HEADER_TEXT_COLOR },
    },
    fill: {
      fgColor: { rgb: EXCEL_HEADER_BG_COLOR },
    },
  };

  const mergeHeaderStyle = (cellStyle: Record<string, any> = {}) => {
    const mergedStyle: Record<string, any> = {
      ...headDefaultStyle,
      ...cellStyle,
    };
    mergedStyle.font = {
      ...(headDefaultStyle as Record<string, any>).font,
      ...(cellStyle as Record<string, any>)?.font,
      bold: true,
    };
    return mergedStyle;
  };

  /**
   * 设置单元格样式（统一处理）
   */
  const setCellStyle = (
    cell: any,
    isHeader: boolean,
    colIndex: number,
  ): void => {
    const useWrap =
      !isHeader && Boolean(columns?.[colIndex]?.wrapText);
    const bodyAlignBlock = useWrap ? wrapTextBodyAlign : defaultStyleAlign;
    const style = isHeader
      ? headDefaultStyle
      : {
        ...bodyAlignBlock,
        ...defaultStyleFont,
        ...defaultStyleBorder,
      };
    (cell as any).s = isHeader
      ? mergeHeaderStyle((cell as any)?.s)
      : { ...style, ...(cell as any)?.s };
  };

  // 计算表头行数
  const headerRowCount = headerRows ? headerRows.length : 1;

  for (let i = data.length - 1;i >= 0;i--) {
    const isHeader = i < headerRowCount;
    const row = data[i] as Record<string, any>;
    cols.forEach((k, colIndex) => {
      if (typeof row[k] === 'object' && row[k] !== null && 'v' in (row[k] as object)) {
        setCellStyle(row[k], isHeader, colIndex);
      } else {
        row[k] = formatExcelCommon(row[k] ?? '');
        setCellStyle(row[k], isHeader, colIndex);
      }
    });
  }
};

/**
 * 设置 Excel 默认列宽（从 utils.ts 复制）
 */
const setExportExcelDefaultColWidths = (workSheet: any, data: any[]): void => {
  if (!data || data.length === 0) return;

  const colWidths: { wpx: number; }[] = [];
  const colCount = Object.keys(data[0] ?? {}).length;

  for (let i = 0;i < colCount;i++) {
    colWidths.push({ wpx: EXCEL_DEFAULT_COL_WIDTH });
  }

  workSheet['!cols'] = colWidths;
};

/**
 * 将格式化结果转换为字符串（用于合并显示）
 * @param formatted 格式化结果（可能是 Excel 格式对象或普通值）
 * @param format 原始格式字符串（用于日期类型转换）
 * @returns 格式化后的字符串
 */
const formatToDisplayString = (
  formatted: any,
  format?: string | ((value: any, record: any) => any),
): string => {
  // 这里日期处理 和单独列（主列）日期处理不一致是因为，合并列的时候，不能是纯日期格式，只能是文本格式
  if (typeof formatted === 'object' && formatted !== null && 'v' in formatted) {
    if (formatted.t === 'd') {
      // 日期类型：使用原始格式转换为 moment 格式
      const momentFormat = typeof format === 'string' && format
        ? convertExcelDateFormatToMoment(format)
        : 'YYYY-MM-DD';
      return moment(formatted.v).format(momentFormat);
    }
    return String(formatted.v);
  }
  return String(formatted ?? '');
};

/**
 * 展开分组表头，只保留有数据的叶子列。默认不启用，避免影响其他下载。
 */
const flattenDownloadGroupColumns = (columns: any[] = []): any[] =>
  columns.flatMap((column) =>
    Array.isArray(column?.children) && column.children.length
      ? flattenDownloadGroupColumns(column.children)
      : [column],
  );

/**
 * 从 CommonTable 组件获取的数据转换为下载配置
 * 支持 tagKey 配置：可以为列添加额外的标签信息列
 */
export const convertCommonTableToDownloadConfig = (
  dataSource: any[],
  columns: (ArtColumn & { nameEn?: string; nameCn?: string; downLoadWidth?: number; width?: number; })[],
  tabName: string,
  isEN: boolean,
  options?: { flattenGroupColumns?: boolean },
): ITabDataConfig => {
  const downloadColumns: IColumnFormatConfig[] = [];
  const sourceColumns = options?.flattenGroupColumns
    ? flattenDownloadGroupColumns(columns)
    : columns;

  sourceColumns?.forEach((column) => {
    const { cell: cellConfig = {} } = column.features || {};
    const { tagKey, valueMapping } = cellConfig;

    let format = column.features?.downLoadFormat ?? column.features?.downloadFormat;

    // 如果配置了 valueMapping，使用 mapping 转换值
    if (valueMapping) {
      const originalFormat = format;
      format = (value: unknown, record: unknown) => {
        const mapping = valueMapping[String(value)];
        if (mapping) {
          return getName(isEN, mapping.label, mapping.labelCn);
        }
        if (originalFormat) {
          return formatValueByRule(value, originalFormat, record, isEN);
        }
        return value;
      };
    }

    // 主列配置
    const mainColumn: IColumnFormatConfig = {
      code: column.code || '',
      name: getName(isEN, (column as any)?.nameEn, (column as any)?.nameCn) || column.name || '',
      format,
      width: (column as any)?.downLoadWidth || EXCEL_DEFAULT_COL_WIDTH,
    };

    // 如果有 tagKey，处理合并或单独列
    if (tagKey) {
      const tagKeyConfig = (cellConfig as any)?.tagKeyConfig || {};
      const mergeToMainColumn = (cellConfig as any)?.mergeToMainColumn ?? true;

      if (mergeToMainColumn) {
        // 合并到主列显示
        const originalFormat = mainColumn.format;
        const tagKeyFormat = tagKeyConfig.format || '@';

        mainColumn.format = (value: any, record: any) => {
          // 格式化主值
          const mainFormatted = formatValueByRule(value, originalFormat, record);
          const mainValueStr = formatToDisplayString(mainFormatted, originalFormat);

          // 格式化 tagKey 值
          const tagValue = record?.[tagKey] || '';
          const tagFormatted = formatValueByRule(tagValue, tagKeyFormat, record);
          const tagValueStr = formatToDisplayString(tagFormatted, tagKeyFormat);

          // 合并显示
          return tagValueStr ? `${mainValueStr} ${tagValueStr}` : mainValueStr;
        };
        downloadColumns.push(mainColumn);
      } else {
        // 单独增加一列
        downloadColumns.push(mainColumn);

        // 生成 tagKey 列名（支持国际化）
        const tagKeyName = (tagKeyConfig.nameEn || tagKeyConfig.nameCn || tagKeyConfig.name)
          ? getName(isEN, tagKeyConfig.nameEn, tagKeyConfig.nameCn) || tagKeyConfig.name || ''
          : tagKey;

        downloadColumns.push({
          code: tagKey,
          name: tagKeyName,
          format: tagKeyConfig.format || '@',
          width: tagKeyConfig.width || EXCEL_DEFAULT_COL_WIDTH,
        });
      }
    } else {
      // 没有 tagKey，直接添加主列
      downloadColumns.push(mainColumn);
    }
  });

  return {
    tabName,
    dataSource,
    columns: downloadColumns,
  };
};
/**
 * 通用下载函数（支持单个或多个 tab）
 * 统一使用 tabs 数组，单个 tab 只是数组长度为 1 的情况
 */
export const downloadExcel = async (config: IDownloadConfig, isEN?: boolean): Promise<void> => {
  try {
    // 参数校验：确保 tabs 数组不为空
    if (!config.tabs || config.tabs.length === 0) {
      throw new Error('tabs 数组不能为空，至少需要提供一个 tab 配置');
    }
    await downloadExcelInternal(config, isEN);
  } catch (error) {
    console.error('下载 Excel 失败:', error instanceof Error ? error.message : error);
    throw error;
  }
};
